import { Alert } from '@sr/ui-library';
import { useState } from 'react';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import {
  isCreatedAfterMsr3Cutoff,
  resolveMsr3ReturnBannerCopy,
} from '../constant/msr3ReturnBannerCopy';
import { useCanReturnToMsr2 } from '../hooks/useCanReturnToMsr2';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { reduxServices } from '../reducers/reduxServices';
import { useTypedSelector } from '../stateStore';
import { getMsr2Base } from '../utils/embedAuth';
import { ReturnToMsr2Dialog } from './ReturnToMsr2Dialog';

export type ReturnToMsr2BannerProps = {
  /** Width and spacing, owned by the mount site. */
  className?: string;
};

/**
 * MSR2's Overview page. It is served under a `/v2/` base href, and no workspace
 * id is passed: MSR2 resolves the workspace from its own member API rather than
 * from the URL.
 *
 * `skipPreLogin` stops MSR2 bouncing an unauthenticated member straight back
 * here to log in, which would be a loop.
 */
const buildReturnUrl = () => `${getMsr2Base()}/v2/overview?skipPreLogin=true`;

/**
 * Standing notice that the member is on the redesigned app, with a way back to
 * MSR2 (APP-2283, register items 8 and 9).
 *
 * Deliberately short-lived — roughly four weeks, from the MSR3 soft launch on
 * 24 September 2026 to the hard launch on 22 October 2026. After the hard
 * launch there is no previous version to return to, so the whole feature comes
 * out rather than being switched off: this component, ReturnToMsr2Dialog,
 * msr3ReturnBannerCopy, hubspotFormSubmission, useCanReturnToMsr2, the mount in
 * MSR3BaseLayout, the `msr3.customerApp.returnToMsr2Banner` flag and its
 * Parameter Store keys.
 *
 * If a standing notice is still wanted after that, AnnouncementBanner already
 * covers the call-to-action-free version driven by Parameter Store copy. This
 * one exists separately only because it needs the CTA, the dialog and the
 * eligibility rules, none of which outlive the cutover.
 */
export const ReturnToMsr2Banner = ({ className }: ReturnToMsr2BannerProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const settingsConfiguration = useTypedSelector(
    reduxServices.setting.selectors.configuration,
  );
  const { auth, currentUser } = useCurrentUserAuth();
  const [isEnabled] = useFeatureFlag(FEATURE_FLAGS.MSR3_RETURN_TO_MSR2_BANNER);

  // Workspace age is handled inside useCanReturnToMsr2, alongside the rest of
  // the per-workspace rules.
  const isTooNew = isCreatedAfterMsr3Cutoff(currentUser?.created);
  const isEligible = isEnabled && !!auth?.currentUserIsExternal && !isTooNew;

  // MSR2 gates its Overview page, and every member pane, on the royalties
  // privilege, so without it there is nothing to return to. Passing the whole
  // predicate keeps staff and post-cutoff members from paying for a lookup
  // whose answer is discarded below.
  const canReturn = useCanReturnToMsr2(isEligible);

  if (!isEligible || !canReturn) return null;

  const copy = resolveMsr3ReturnBannerCopy(settingsConfiguration);

  return (
    <div className={className}>
      <Alert
        title={copy.title}
        actionButtonProps={{
          children: copy.actionLabel,
          onClick: () => setIsDialogOpen(true),
        }}
        hasActionButton
        // Overrides Alert's built-in role="alert". A standing notice, not an
        // alert — the shell remounts on navigation and a live region would read
        // it out again every time.
        role="note"
        variant="info"
      >
        <p className="whitespace-pre-line text-sm">{copy.body}</p>
      </Alert>

      <ReturnToMsr2Dialog
        heading={copy.confirmTitle}
        body={copy.confirmBody}
        email={currentUser?.emailAddress}
        footer={copy.confirmFooter}
        open={isDialogOpen}
        returnUrl={buildReturnUrl()}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
};
